import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { subjectUnion } from "../../../utils/content/library";
import { fetch_subject_content } from "../../../actions/SubjectContent/dispatchers";
import { EVENT_SUBJECT } from "../constants";
import useQuery from "../../../hooks/useQuery";
import { cardNode } from "../../../fetch/graphql/fragments/cardNode";
import { transformAllContentItems } from "../../../utils/dataTransform/library";

const QUERY = `
query allContentItems( $pageSize: Int, $categorySlugs:[String!]) {
    allContentItems( pageSize: $pageSize, categorySlugs: $categorySlugs, targets: [WEB,ALL]) {
        nodes {
            ...cardNode
        }
    },
   
  } ${cardNode}
`;

//Spoke to WPP and they suggested this, it is a temporary workaround, until we find a way to deprecate departments in CQW
const transformCategoriesToDepartments = (contentItem) => {
    const { departments, categories } = contentItem;

    if (departments?.sectionLabels?.length || departments?.sectionList?.length)
        return contentItem.departments;

    const sectionLabels = new Set();
    const sectionList = new Set();

    categories?.forEach((category) => {
        if (
            category.slug !== "sports" &&
            category.path !== "Sports/Olympics" &&
            category.slug !== "winter-olympics" &&
            category.slug !== "winter-olympics-highlights" &&
            category.slug !== "sports-highlights" &&
            category.slug.search("winter-olympics-replays") === -1 &&
            category.slug !== "sports-replays"
        ) {
            if (!sectionLabels.has(category.name.trim())) {
                sectionLabels.add(category.name.trim());
                sectionList.add(category.slug);
            }
        }
    });

    return {
        sectionLabels: [sectionLabels[0]] || [],
        sectionList: [sectionList[0]] || [],
    };
};

export const useContentItem = (sports, pageSize) => {
    const minPubDate = useMemo(() => (Date.now() / 1000) | 0, []);
    const categorySlugs = useMemo(
        () =>
            sports
                .filter((sport) => !!sport.categorySlug)
                .map((sport) => sport.categorySlug),
        sports
    );

    const variables = {
        categorySlugs: categorySlugs,
        minPubDate,
        pageSize,
    };

    //updating the query to use CategorySlugs array instead of separate calls for each category
    const result = useQuery(QUERY, variables);

    const items = useMemo(() => {
        if (result.isLoading) {
            return [];
        }
        if (result.error) {
            return [];
        }
        const contentList = transformAllContentItems(
            result?.response?.allContentItems.nodes
        );

        const items = contentList.map((item, index) => {
            //using result?.response?.allContentItems.nodes because the transform function gets rid of categories.
            const departments = transformCategoriesToDepartments(
                result?.response?.allContentItems.nodes[index]
            );

            return {
                ...item,
                description: undefined,
                showTimestamp: true,
                metadataId: item.id,
                departments: departments,
                image: { url: item?.imageURL },
            };
        });

        return items;
    }, [result.isLoading, result.response, result.error]);

    return {
        isLoading: result.isLoading,
        items,
    };
};

export const usePoolpartySubjectContent = (sports, pageSize) => {
    const dispatch = useDispatch();
    const content = useSelector((state) => state.subjectContent);
    const subjectQuery = useMemo(() => {
        const filteredSports = sports.filter((sport) => !!sport.path);
        return `${EVENT_SUBJECT}^(${subjectUnion(
            filteredSports.map((s) => s.path)
        )})`;
    }, sports);
    const isLoading = content.subjects !== subjectQuery;
    useEffect(() => {
        if (!isLoading) {
            return;
        }

        dispatch(fetch_subject_content(subjectQuery, pageSize));
    }, [subjectQuery, isLoading]);

    const items = isLoading ? [] : content.items;
    return { items, isLoading };
};

const getDate = (item) => {
    return item.airDate ?? item.updateTime ?? item.updatedAt ?? 0;
};

const sortItems = (items, order) => {
    return items.sort((a, b) => {
        return order === "desc"
            ? getDate(b) - getDate(a)
            : getDate(a) - getDate(b);
    });
};

export const useSportsContent = (sports, pageSize) => {
    // Some sports have so many events that we need to fetch extra
    // to ensure the items aren't all live events scheduled for the future
    const safeFetchSize = pageSize * 2;
    const contentItems = useContentItem(sports, safeFetchSize);

    const poolparty = usePoolpartySubjectContent(sports, safeFetchSize);

    const isLoading = contentItems.isLoading || poolparty.isLoading;

    const items = useMemo(() => {
        if (isLoading) {
            return [];
        }

        /*
            What This Code Does:
            1. Combine and sort all items in ascending order of date, filtering out duplicates
            2. Find the index of "now" - where all subsequent items are upcoming live events
            3. Splice out the first four upcoming live events (to use in the first row of results)
            4. Slice the remaining 12 items from the 'past' items (if not enough, upcoming live items will be used)

            We min-max the indexes to ensure we don't go out of bounds.
            
            The end result is an array of pageSize items, with the first four being the soonest upcoming live events (in ascending order of date)
        */

        const itemsFetched = [...poolparty.items, ...contentItems.items.flat()];

        const idsLookup = new Map();
        const itemsWithoutDuplicates = itemsFetched.filter((item) => {
            if (idsLookup.has(item.id)) {
                return false;
            }
            idsLookup.set(item.id, item);
            return true;
        });

        const itemsFetchedAsc = sortItems(
            Array.from(itemsWithoutDuplicates),
            "asc"
        );
        const now = Date.now();
        let indexOfNow = itemsFetchedAsc.findIndex((item) => {
            return getDate(item) > now;
        });
        if (indexOfNow === -1) {
            indexOfNow = itemsFetchedAsc.length - 4;
        }

        const safeIndexForUpcomingItems = Math.min(
            indexOfNow,
            itemsFetchedAsc.length - 4
        );
        const firstFourItemsAsc = itemsFetchedAsc.splice(
            safeIndexForUpcomingItems,
            4
        );

        const sliceStartIndex = Math.max(
            0,
            safeIndexForUpcomingItems - (pageSize - 4)
        );
        const sliceEndIndex = Math.min(
            sliceStartIndex + (pageSize - 4),
            itemsFetchedAsc.length
        );
        const restOfItems = itemsFetchedAsc.slice(
            sliceStartIndex,
            sliceEndIndex
        );
        const restOfItemsSortedDesc = sortItems(restOfItems, "desc");

        return [...firstFourItemsAsc, ...restOfItemsSortedDesc];
    }, [isLoading]);

    return {
        isLoading,
        items,
    };
};
